import type { AxiosError } from "axios";
import {
  ApiError,
  type FieldErrorDetail,
  type FieldErrorDetails,
  type FieldErrors,
} from "../../errors";

/** Keys that carry the machine-readable code or the envelope containers. */
const CODE_KEYS = ["code", "detail"] as const;
const ENVELOPE_KEYS = new Set(["code", "detail", "fields", "meta"]);

function normalizeEntry(value: unknown): FieldErrorDetail | null {
  if (typeof value === "string" && value) return { code: value };
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const record = value as Record<string, unknown>;
    if (typeof record.code === "string" && record.code) {
      return {
        code: record.code,
        ...(record.params && typeof record.params === "object"
          ? { params: record.params as Record<string, unknown> }
          : {}),
      };
    }
  }
  return null;
}

/**
 * Pull the machine-readable code and field errors out of a response body.
 *
 * New responses use `{ code, detail, fields, meta }`; old responses may still
 * use flat field arrays. Both are accepted so the frontend survives the
 * compatibility window without per-screen changes.
 */
function extractBody(body: unknown): {
  code: string | null;
  fieldErrors: FieldErrors | null;
  fieldErrorDetails: FieldErrorDetails | null;
  meta: Record<string, unknown> | null;
} {
  if (!body || typeof body !== "object") {
    return {
      code: null,
      fieldErrors: null,
      fieldErrorDetails: null,
      meta: null,
    };
  }
  const record = body as Record<string, unknown>;

  let code: string | null = null;
  for (const key of CODE_KEYS) {
    const value = record[key];
    if (typeof value === "string") {
      code = value;
      break;
    }
  }

  const fieldErrors: FieldErrors = {};
  const fieldErrorDetails: FieldErrorDetails = {};
  let hasFieldErrors = false;

  const addField = (field: string, values: unknown[]) => {
    const entries = values
      .map(normalizeEntry)
      .filter((entry): entry is FieldErrorDetail => entry !== null);
    if (entries.length === 0) return;
    fieldErrors[field] = entries.map((entry) => entry.code);
    fieldErrorDetails[field] = entries;
    hasFieldErrors = true;
  };

  const nestedFields = record.fields;
  if (
    nestedFields &&
    typeof nestedFields === "object" &&
    !Array.isArray(nestedFields)
  ) {
    for (const [field, value] of Object.entries(nestedFields)) {
      if (Array.isArray(value)) addField(field, value);
    }
  }

  // Compatibility: older responses put field arrays at the response root.
  // Skip this when the envelope containers exist; their root-level meta copies
  // may also be arrays and must not be mistaken for field errors.
  if (!("fields" in record) && !("meta" in record)) {
    for (const [key, value] of Object.entries(record)) {
      if (ENVELOPE_KEYS.has(key)) continue;
      if (
        Array.isArray(value) &&
        value.every((item) => typeof item === "string")
      ) {
        addField(key, value);
      }
    }
  }

  const meta =
    record.meta &&
    typeof record.meta === "object" &&
    !Array.isArray(record.meta)
      ? (record.meta as Record<string, unknown>)
      : null;

  return {
    code,
    fieldErrors: hasFieldErrors ? fieldErrors : null,
    fieldErrorDetails: hasFieldErrors ? fieldErrorDetails : null,
    meta,
  };
}

/** Normalize any axios error into the shared {@link ApiError}. status 0 == no response. */
export function toApiError(error: AxiosError): ApiError {
  const status = error.response?.status ?? 0;
  const body = error.response?.data ?? null;
  const { code, fieldErrors, fieldErrorDetails, meta } = extractBody(body);
  return new ApiError({
    status,
    code,
    fieldErrors,
    fieldErrorDetails,
    meta,
    body,
    message: error.message,
  });
}
